import { PARTY_ROLE, PARTY_ROLE_DISPLAY, PARTY_STATUS, type PartyRoleId } from '@steerfi/shared';
import { AlertCircle } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { ProviderApiError, type ProviderApiRow, updateProvider } from '@/api/providers';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { flattenZodErrors, providerFormSchema } from '@/schemas/provider-form-schema';

interface EditProviderModalProps {
  open: boolean;
  provider: ProviderApiRow | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const EditProviderModal: React.FC<EditProviderModalProps> = ({
  open,
  provider,
  onOpenChange,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [codeWarningVisible, setCodeWarningVisible] = useState(false);
  const [provider_name, setProviderName] = useState('');
  const [provider_code, setProviderCode] = useState('');
  const [license_number, setLicenseNumber] = useState('');
  const [party_role_id, setPartyRoleId] = useState<number | ''>('');
  const [party_status_id, setPartyStatusId] = useState<number | ''>('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [rootError, setRootError] = useState<string | null>(null);

  useEffect(() => {
    if (provider && open) {
      setProviderName(provider.provider_name);
      setProviderCode(provider.provider_code);
      setLicenseNumber(provider.license_number ?? '');
      setPartyRoleId(provider.party_role_id);
      setPartyStatusId(provider.status_id);
      setCodeWarningVisible(false);
      setFieldErrors({});
      setRootError(null);
    }
  }, [provider, open]);

  const resetForm = () => {
    setProviderName('');
    setProviderCode('');
    setLicenseNumber('');
    setPartyRoleId('');
    setPartyStatusId('');
    setFieldErrors({});
    setRootError(null);
    setCodeWarningVisible(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!provider) return;
    setFieldErrors({});
    setRootError(null);
    const parsed = providerFormSchema.safeParse({
      provider_name,
      provider_code,
      license_number,
      party_role_id: party_role_id === '' ? undefined : party_role_id,
      party_status_id: party_status_id === '' ? undefined : party_status_id,
    });
    if (!parsed.success) {
      setFieldErrors(flattenZodErrors(parsed.error));
      return;
    }
    try {
      setSubmitting(true);
      await updateProvider(provider.party_id, {
        provider_name: parsed.data.provider_name,
        provider_code: parsed.data.provider_code,
        license_number: parsed.data.license_number || null,
        party_role_id: parsed.data.party_role_id,
        party_status_id: parsed.data.party_status_id,
      });
      onSuccess();
      handleOpenChange(false);
    } catch (e: unknown) {
      if (e instanceof ProviderApiError && e.status === 409) {
        const msg =
          (typeof e.body?.message === 'string' && e.body.message) ||
          `Provider with code '${parsed.data.provider_code}' already exists`;
        setFieldErrors((prev) => ({ ...prev, provider_code: msg }));
      } else {
        setRootError(e instanceof Error ? e.message : 'Failed to update provider');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Provider</DialogTitle>
          <DialogDescription>Update provider details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-provider-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-provider-name"
              autoFocus
              value={provider_name}
              onChange={(ev) => setProviderName(ev.target.value)}
              aria-invalid={!!fieldErrors.provider_name}
            />
            {fieldErrors.provider_name && (
              <p className="text-sm text-destructive">{fieldErrors.provider_name}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-provider-code">
              Code <span className="text-destructive">*</span>
            </Label>
            {codeWarningVisible && (
              <Alert variant="default" className="bg-amber-50 border-amber-200 py-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-xs text-amber-800">
                  Changing the code will affect all existing integrations linked to this provider.
                  Proceed with caution.
                </AlertDescription>
              </Alert>
            )}
            <Input
              id="edit-provider-code"
              value={provider_code}
              onFocus={() => setCodeWarningVisible(true)}
              onChange={(ev) => setProviderCode(ev.target.value)}
              aria-invalid={!!fieldErrors.provider_code}
            />
            {fieldErrors.provider_code && (
              <p className="text-sm text-destructive">{fieldErrors.provider_code}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-provider-license">License #</Label>
            <Input
              id="edit-provider-license"
              value={license_number}
              onChange={(ev) => setLicenseNumber(ev.target.value)}
              aria-invalid={!!fieldErrors.license_number}
            />
            {fieldErrors.license_number && (
              <p className="text-sm text-destructive">{fieldErrors.license_number}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                onValueChange={(v) => setPartyRoleId(Number(v))}
                value={party_role_id === '' ? '' : String(party_role_id)}
              >
                <SelectTrigger aria-invalid={!!fieldErrors.party_role_id}>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(PARTY_ROLE) as [string, number][]).map(([, value]) => (
                    <SelectItem key={value} value={String(value)}>
                      {PARTY_ROLE_DISPLAY[value as PartyRoleId] ?? String(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.party_role_id && (
                <p className="text-sm text-destructive">{fieldErrors.party_role_id}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                onValueChange={(v) => setPartyStatusId(Number(v))}
                value={party_status_id === '' ? '' : String(party_status_id)}
              >
                <SelectTrigger aria-invalid={!!fieldErrors.party_status_id}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(PARTY_STATUS.ACTIVE)}>Active</SelectItem>
                  <SelectItem value={String(PARTY_STATUS.INACTIVE)}>Inactive</SelectItem>
                </SelectContent>
              </Select>
              {fieldErrors.party_status_id && (
                <p className="text-sm text-destructive">{fieldErrors.party_status_id}</p>
              )}
            </div>
          </div>
          {rootError && <p className="text-sm text-destructive">{rootError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
